import React from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, CheckCircle2, Wallet, TrendingUp, Pencil, Copy, Eye, Send, Trash2, Download } from "lucide-react";
import { fmtCurrency, fmtDate, fmtDateTime, quoteTotal, trackingUrlFor } from "@/lib/quote-helpers";
import { docLabel, offerLabelPlural, STATUS_META, STATUS_ORDER } from "@/lib/constants";
import { RecurringInvoicesPanel } from "@/components/RecurringInvoicesPanel";
import { BusinessCasesPanel } from "@/components/BusinessCasesPanel";
import { CatalogPanel } from "@/components/CatalogPanel";
import { resolveOrgCurrency } from "@/lib/currency-context";

export const AngeboteTab = ({
  company = {}, quotes, datevFrom, setDatevFrom, datevTo, setDatevTo,
  loadOne, convertOneToInvoice, exportDatev, changeStatus, duplicateOne, removeOne,
}) => (
  <>
            {(() => {
              const closedStatuses = ["declined", "cancelled", "paid"];
              const openReceivableStatuses = ["issued", "sent", "opened", "overdue", "partially_paid"];
              const activeDocuments = quotes.filter((q) => !closedStatuses.includes(q.status));
              const closedDocuments = quotes.filter((q) => closedStatuses.includes(q.status));
              const angebote = activeDocuments.filter((q) => q.dokument_typ === "angebot");
              const openOffers = angebote.filter((q) => q.status === "draft" || q.status === "sent");
              const accepted = activeDocuments.filter((q) => q.dokument_typ === "angebot" && q.status === "accepted");
              const acceptedValue = accepted.reduce((s, q) => s + quoteTotal(q), 0);
              const invoiceTypes = ["rechnung", "anzahlungsrechnung", "schlussrechnung"];
              const openInvoices = activeDocuments.filter((q) => invoiceTypes.includes(q.dokument_typ) && openReceivableStatuses.includes(q.status));
              const openInvoiceValue = openInvoices.reduce((s, q) => s + quoteTotal(q), 0);
              const cards = [
                { key: "open-offers", label: `Offene ${offerLabelPlural(company)}`, value: String(openOffers.length), Icon: FileText, color: "#3B82F6" },
                { key: "accepted-value", label: "Angenommen (Wert)", value: fmtCurrency(acceptedValue, resolveOrgCurrency(company)), Icon: CheckCircle2, color: "#10B981" },
                { key: "open-invoices", label: "Offene Rechnungen", value: String(openInvoices.length), Icon: Wallet, color: "#F59E0B" },
                { key: "open-invoice-value", label: "Offener Betrag", value: fmtCurrency(openInvoiceValue, resolveOrgCurrency(company)), Icon: TrendingUp, color: "#059669" },
              ];
              return (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="dashboard-kpis">
                  {cards.map((c) => (
                    <Card key={c.key} className="p-4 bg-white border-[var(--brand-border)]" data-testid={`kpi-${c.key}`}>
                      <div className="flex items-center gap-2 text-[var(--brand-muted)] text-xs mb-1">
                        <c.Icon className="w-4 h-4" style={{ color: c.color }} />
                        {c.label}
                      </div>
                      <div className="text-2xl font-semibold text-[var(--brand-ink)]" data-testid={`kpi-${c.key}-value`}>
                        {c.value}
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })()}
            {quotes.some((q) => q.dokument_typ === "angebot" && q.status === "accepted") && (
              <Card className="p-5 mb-4 bg-white border-2 border-[var(--brand-primary)]" data-testid="accepted-overview">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-[Playfair_Display] font-semibold text-[var(--brand-ink)]">
                    Angenommene {offerLabelPlural(company)}
                  </h3>
                  <span className="text-[11px] font-bold bg-green-600 text-white rounded-full px-2 py-0.5" data-testid="accepted-count">
                    {quotes.filter((q) => q.dokument_typ === "angebot" && q.status === "accepted").length}
                  </span>
                  <span className="text-xs text-[var(--brand-muted)] ml-1">– bereit zur Rechnungsstellung</span>
                </div>
                <div className="divide-y divide-[var(--brand-border)]">
                  {quotes.filter((q) => q.dokument_typ === "angebot" && q.status === "accepted").map((q) => (
                    <div key={q.id} className="py-3 flex items-center justify-between flex-wrap gap-3" data-testid={`accepted-row-${q.id}`}>
                      <div className="flex items-center gap-3">
                        {q.customer_signature && (
                          <img
                            src={q.customer_signature}
                            alt="Unterschrift"
                            className="h-10 w-auto max-w-[120px] object-contain border border-[var(--brand-border)] rounded bg-white"
                            data-testid={`accepted-sig-${q.id}`}
                          />
                        )}
                        <div>
                          <div className="font-semibold text-[var(--brand-ink)] text-sm">
                            {q.angebot_nr || "—"} · {q.kunde_firma || q.kunde_name || "Unbenannt"}
                          </div>
                          <div className="text-xs text-[var(--brand-muted)] flex items-center gap-3 flex-wrap">
                            <span>{fmtCurrency(quoteTotal(q), q.currency || resolveOrgCurrency(company))}</span>
                            {q.customer_signed_at && (
                              <span className="flex items-center gap-1 text-green-700" data-testid={`accepted-at-${q.id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Angenommen {fmtDateTime(q.customer_signed_at)}
                              </span>
                            )}
                            {q.customer_signed_name && <span>von {q.customer_signed_name}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadOne(q.id)}
                          data-testid={`accepted-open-${q.id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" /> Öffnen
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => convertOneToInvoice(q.id)}
                          className="bg-[var(--brand-ink)] hover:bg-black text-white"
                          data-testid={`accepted-to-invoice-${q.id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" /> In Rechnung umwandeln
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            <Card className="p-4 mb-4 bg-white border-[var(--brand-border)]" data-testid="datev-export-bar">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-sm font-semibold text-[var(--brand-ink)]">
                  DATEV-Export (Steuerberater)
                </div>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-[var(--brand-muted)]">von</span>
                  <Input type="date" value={datevFrom} onChange={(e) => setDatevFrom(e.target.value)}
                    className="w-36 sm:w-40" data-testid="datev-from" />
                  <span className="text-[var(--brand-muted)]">bis</span>
                  <Input type="date" value={datevTo} onChange={(e) => setDatevTo(e.target.value)}
                    className="w-36 sm:w-40" data-testid="datev-to" />
                </div>
                <Button size="sm" onClick={exportDatev}
                  className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                  data-testid="datev-export-btn">
                  <Download className="w-4 h-4 mr-1" /> EXTF-Buchungsstapel
                </Button>
              </div>
            </Card>
            <CatalogPanel />
            <Card className="p-5 bg-white border-[var(--brand-border)]">
              {quotes.length === 0 ? (
                <div className="text-center py-12 text-[var(--brand-muted)]">
                  Noch keine {offerLabelPlural(company)} gespeichert.
                </div>
              ) : (
                <div className="divide-y divide-[var(--brand-border)]">
                  {quotes.filter((q) => !["declined", "cancelled", "paid"].includes(q.status)).map((q) => (
                    <div
                      key={q.id}
                      className="py-3 flex items-center justify-between flex-wrap gap-2"
                      data-testid={`quote-row-${q.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-[Playfair_Display] font-semibold text-[var(--brand-ink)] flex items-center gap-2 flex-wrap break-words">
                          <span
                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                              q.dokument_typ === "angebot"
                                ? "bg-[var(--brand-primary)] text-white"
                                : "bg-[var(--brand-ink)] text-white"
                            }`}
                          >
                            {docLabel(q.dokument_typ, company)}
                          </span>
                          {q.angebot_nr || "—"} ·{" "}
                          {q.kunde_firma || q.kunde_name || "Unbenannt"}
                          <span
                            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full text-white"
                            style={{ background: (STATUS_META[q.status] || STATUS_META.draft).color }}
                            data-testid={`status-badge-${q.id}`}
                          >
                            {(STATUS_META[q.status] || STATUS_META.draft).label}
                          </span>
                        </div>
                        <div className="text-xs text-[var(--brand-muted)] flex items-center gap-3 flex-wrap">
                          <span>{fmtDate(q.angebot_datum)}</span>
                          <span>
                            {fmtCurrency(quoteTotal(q))}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {q.views || 0}
                            {q.last_viewed_at &&
                              ` · ${fmtDateTime(q.last_viewed_at)}`}
                          </span>
                          {q.sent_at && (
                            <span className="flex items-center gap-1 text-[var(--brand-primary)]" data-testid={`sent-at-${q.id}`}>
                              <Send className="w-3 h-3" /> Gesendet {fmtDateTime(q.sent_at)}
                            </span>
                          )}
                          <select
                            value={q.status || "draft"}
                            onChange={(e) => changeStatus(q.id, e.target.value)}
                            className="text-xs border border-[var(--brand-border)] rounded px-1.5 py-0.5 bg-white"
                            data-testid={`status-select-${q.id}`}
                          >
                            {STATUS_ORDER.map((s) => (
                              <option key={s} value={s}>{STATUS_META[s].label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const url = trackingUrlFor(q.public_token);
                            try {
                              await navigator.clipboard.writeText(url);
                              toast.success("Tracking-Link kopiert");
                            } catch {
                              toast.error("Konnte nicht kopieren");
                            }
                          }}
                          data-testid={`btn-copy-${q.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => loadOne(q.id)}
                          data-testid={`btn-load-${q.id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" /> Öffnen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => duplicateOne(q.id)}
                          data-testid={`btn-duplicate-${q.id}`}
                          title="Duplizieren"
                        >
                          <Copy className="w-4 h-4 mr-1" /> Duplizieren
                        </Button>
                        {q.locked_at ? (
                          <span className="self-center text-xs text-[var(--brand-muted)]" title="Festgeschriebene Rechnungen bleiben nachvollziehbar und werden über Storno oder Gutschrift korrigiert.">
                            Festgeschrieben
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => removeOne(q.id)}
                            data-testid={`btn-delete-${q.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {quotes.filter((q) => ["declined", "cancelled", "paid"].includes(q.status)).length > 0 && (
              <details className="mt-4 rounded-lg border border-[var(--brand-border)] bg-white p-4" data-testid="closed-documents-archive">
                <summary className="cursor-pointer text-sm font-semibold text-[var(--brand-ink)]">
                  Abgelehnte, stornierte und bezahlte Dokumente ({quotes.filter((q) => ["declined", "cancelled", "paid"].includes(q.status)).length})
                </summary>
                <p className="mt-2 text-xs text-[var(--brand-muted)]">
                  Diese Dokumente sind abgeschlossen und zählen nicht mehr als offene Forderung. Festgeschriebene Rechnungen bleiben zur Nachvollziehbarkeit erhalten.
                </p>
                <div className="mt-3 divide-y divide-[var(--brand-border)]">
                  {quotes.filter((q) => ["declined", "cancelled", "paid"].includes(q.status)).map((q) => (
                    <div key={q.id} className="py-2 flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-sm text-[var(--brand-ink)]">
                        <span className="font-medium">{docLabel(q.dokument_typ, company)} {q.angebot_nr || "—"}</span>
                        <span className="text-[var(--brand-muted)]"> · {q.kunde_firma || q.kunde_name || "Unbenannt"} · {fmtCurrency(quoteTotal(q))} · {(STATUS_META[q.status] || STATUS_META.draft).label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {q.locked_at && <span className="text-xs text-[var(--brand-muted)]">Festgeschrieben</span>}
                        <Button size="sm" variant="outline" onClick={() => loadOne(q.id)} data-testid={`closed-open-${q.id}`}>
                          <Pencil className="w-4 h-4 mr-1" /> Öffnen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

    <RecurringInvoicesPanel quotes={quotes} onOpenInvoice={loadOne} />
    <BusinessCasesPanel quotes={quotes} onOpenQuote={loadOne} company={company} />
  </>
);
